import {useCallback} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {Box, Checkbox, Flex, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useCurrency} from '@salesforce/retail-react-app/app/hooks';

import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
import {useProductDetailContentful} from '@amblique/king-living/app/context/product-detail-contentful-context';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';
import {
  getKingGuardOptionValue,
  KING_GUARD_INCLUDED_INTL,
  setProductIncludesKingGuard,
} from '@amblique/king-living/app/utils/cart-utils';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';

/**
 * KingGuard / Protect Your Cover. Shown when `c_displayKingGuard`; labels from FSD;
 * price and basket `optionItems` use the cover variant (`c_allVariantsData`).
 */
export function ProtectYourCover({product, selectedVariantProduct}) {
  const intl = useIntl();
  const {currency} = useCurrency();
  const styles = useMultiStyleConfig('protectYourCover', {});
  const {committedSelection, commitInlineSelections, setProductsMap} = useCustomizeJourney();
  const contentfulData = useProductDetailContentful();

  if (product?.c_displayKingGuard !== true) return null;

  const hasCoverSelected = Boolean(
    committedSelection?.selections?.cover?.productId ??
    committedSelection?.selections?.cover?.variantId
  );

  const kingGuardApplicable = selectedVariantProduct?.c_kingGuardApplicable === true;

  const isPreselected = committedSelection?.mode === 'set' &&
    setProductIncludesKingGuard(setProductsMap?.[committedSelection?.productId]);

  // KingGuard option data: prefer variant entry from c_allVariantsData (variant-specific
  // pricing); fall back to master `product` (loaded with expand=options on PDP) when
  // c_allVariantsData hasn't been rebuilt yet.
  const kingGuardSourceProduct = selectedVariantProduct?.options?.length > 0 ?
    selectedVariantProduct :
    product;
  const kingGuardValue = getKingGuardOptionValue(kingGuardSourceProduct);
  const kingGuardPrice = kingGuardValue?.price ?? null;
  const formattedKingGuardPrice = kingGuardPrice != null ?
    formatCurrencyHideTrailingZeroCents(intl, kingGuardPrice, currency) :
    null;

  // Only honour the enabled flag when the current variant actually supports KingGuard.
  // Prevents stale checked state when the user switches from an applicable to a
  // non-applicable cover variant without explicitly unchecking.
  // isPreselected overrides: the set includes KingGuard regardless of the toggle state.
  const isEnabled = isPreselected || (kingGuardApplicable &&
    committedSelection?.selections?.optionalAddOns?.kingGuard?.enabled === true);
  const isDisabled = !kingGuardApplicable || !hasCoverSelected || isPreselected;

  const kingGuardTitle = contentfulData?.kingGuardTitle ?? null;
  const kingGuardContent = contentfulData?.kingGuardContent ?? null;

  const handleToggle = useCallback((e) => {
    const current = committedSelection?.selections ?? {};

    commitInlineSelections({
      ...current,
      optionalAddOns: {
        ...current.optionalAddOns,
        kingGuard: {enabled: e.target.checked},
      },
    });
  }, [committedSelection?.selections, commitInlineSelections]);

  const handleToggleFromKeyboard = useCallback(() => {
    if (isDisabled) return;

    const current = committedSelection?.selections ?? {};

    commitInlineSelections({
      ...current,
      optionalAddOns: {
        ...current.optionalAddOns,
        kingGuard: {enabled: !isEnabled},
      },
    });
  }, [commitInlineSelections, committedSelection?.selections, isDisabled, isEnabled]);

  const checkboxLabel = isPreselected ?
    intl.formatMessage(KING_GUARD_INCLUDED_INTL) :
    kingGuardApplicable && hasCoverSelected ?
      intl.formatMessage({defaultMessage: 'Add KingGuard®', id: 'protect_your_cover.label.add'}) :
      intl.formatMessage({defaultMessage: 'Not available for KingGuard®', id: 'protect_your_cover.label.not_available'});

  const showPrice = kingGuardApplicable && formattedKingGuardPrice != null;

  return (
    <Box className="protect-your-cover__root" {...styles.root}>
      {kingGuardTitle && (
        <Text className="protect-your-cover__title" {...styles.title}>
          {kingGuardTitle}
        </Text>
      )}

      {kingGuardContent && (
        <RichTextContent
          className="protect-your-cover__description"
          content={kingGuardContent}
          {...styles.description}
        />
      )}

      <Checkbox
        className="protect-your-cover__checkbox-row"
        isChecked={isEnabled}
        isDisabled={isDisabled}
        onChange={handleToggle}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (isDisabled) return;

          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            handleToggleFromKeyboard();
          }
        }}
        {...styles.checkboxRow}
      >
        <Flex className="protect-your-cover__label-group" {...styles.labelGroup}>
          <Text className="protect-your-cover__label" {...styles.checkboxLabel}>
            {checkboxLabel}
          </Text>

          {showPrice && (
            <Text className="protect-your-cover__price protect-your-cover__label_price" {...styles.price}>
              {`+ ${formattedKingGuardPrice}`}
            </Text>
          )}
        </Flex>
      </Checkbox>
    </Box>
  );
}

ProtectYourCover.displayName = 'ProtectYourCover';

ProtectYourCover.propTypes = {
  product: PropTypes.object.isRequired,
  selectedVariantProduct: PropTypes.object,
};
